/**
 * Platform Admin — Helix VC Internal Only
 *
 * Access (page + sidebar entry) is gated on Helix tenancy — platform==='HELIX'
 * or company_type==='helix' — to match the backend IsHelixUser permission
 * (CR-6351). Superuser-only sections are filtered separately via `superOnly`.
 * Contains platform-level config that clients never see: portal type,
 * menu builder, feature flags, data import, analytics datasets, help layout.
 */
import { useState } from 'react';
import {
  Settings, Zap, Upload, LifeBuoy, BarChart3, Users,
  ChevronRight, Globe, RefreshCw, CalendarClock, Terminal,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  useAdminSettings,
  PlatformTab,
  FeaturesTab,
  DataImportTilesTab,
  HelpLayoutTab,
  AnalyticsTab,
  SupplierCardTab,
  SupplierProfileLayoutTab,
  RegionalDefaultsTab,
  WritebackRulesSection,
  SystemScheduleTab,
  ArrearsSnapshotScheduleSection,
  DashboardSnapshotScheduleSection,
  ForecastSnapshotScheduleSection,
  CommandRunnerTab,
} from '../../components/admin/settings';

type Section =
  | 'platform' | 'features' | 'data-import' | 'analytics'
  | 'supplier-card' | 'supplier-profile'
  | 'regional-defaults' | 'writeback'
  | 'system-schedule' | 'command-runner' | 'help';

// CR-5906: Performance Config removed from Platform Admin — it duplicated
// Admin Setup → Performance, which is the client-owned location for these
// operational settings.
const SECTIONS: {
  key: Section;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  superOnly?: boolean;
  /** Narrower than Helix tenancy: is_superuser OR is_staff. */
  staffOnly?: boolean;
}[] = [
  { key: 'platform', label: 'Platform Config', icon: Settings },
  // CR-6256: the fallback currency/timezone/date format for the whole platform.
  { key: 'regional-defaults', label: 'Regional Defaults', icon: Globe },
  // Sits beside Regional Defaults: both are the currency/locale ground truth.
  // Was a hardcoded top-level Sidebar link, which the navigation rules forbid
  // and which put a read-only internal table above the modules people use.
  { key: 'features', label: 'Features & Modules', icon: Zap },
  // CR-8725: no Dashboard Visibility page. The dashboard tabs sit under the
  // Dashboard items in Platform Config → Navigation Menu Management.
  { key: 'supplier-card', label: 'Supplier Card', icon: Users },
  { key: 'supplier-profile', label: 'Supplier Profile', icon: Users },
  { key: 'data-import', label: 'Data Import', icon: Upload },
  { key: 'analytics', label: 'Analytics Datasets', icon: BarChart3 },
  // Beat timings read-only PLUS the operator-editable arrears snapshot
  // schedule. Its own section, and staff-gated rather than Helix-gated: these rules
  // decide what reaches a customer's ERP, and a pending writeback freezes that
  // field against the import feed until someone drains the queue.
  { key: 'writeback', label: 'ERP Writeback', icon: RefreshCw, staffOnly: true },
  // Read-only: what this instance runs on a timer (beat jobs, snapshot ages) —
  // so the configured timings are visible instead of remembered.
  { key: 'system-schedule', label: 'System Schedule', icon: CalendarClock },
  // Runs registered management commands on this install. Helix-internal by
  // virtue of the page gate; the API additionally admits superusers that are
  // not tenant accounts (IsPlatformOperator).
  { key: 'command-runner', label: 'Command Runner', icon: Terminal },
  { key: 'help', label: 'Help Layout', icon: LifeBuoy, superOnly: true },
];

export default function PlatformAdminPage() {
  const { user } = useAuth();
  const isSuperuser = Boolean(user?.is_superuser);
  // CR-6351: Platform Admin access is keyed on Helix tenancy (platform /
  // company_type), matching the sidebar gate (isVCInternal) and the backend
  // IsHelixUser permission — both of which moved OFF the Django is_staff /
  // is_superuser flags in CR-5367 round 4. This page was the last holdout still
  // gating on those flags, so a Helix admin with no Django staff flag (e.g.
  // jmiller@valuechain.com, company_type='helix') saw the nav item but was
  // wrongly blocked here. is_superuser is still used below to gate the
  // superuser-only sections.
  const isHelixUser = user?.platform === 'HELIX' || user?.company_type === 'helix';
  const [activeSection, setActiveSection] = useState<Section>('platform');
  const {
    platformSettings, setPlatformSettings,
    barrattFlags, setBarrattFlags,
    purchasingTabs, setPurchasingTabs, savePurchasingTabs,
    qualityTabs, setQualityTabs, saveQualityTabs,
    dashboardTabs, setDashboardTabs, savedDashboardTabs,
    supplierDashboardTabs, setSupplierDashboardTabs,
    supplierCardFields, setSupplierCardFields, saveSupplierCardFields,
    dataImportTiles, setDataImportTiles, saveDataImportTiles,
    saving,
    savePlatformSettings, saveDashboardTabs,
  } = useAdminSettings();

  if (!isHelixUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">You do not have access to Platform Admin.</p>
      </div>
    );
  }

  // Mirrors the backend's WRITEBACK_SETTING_KEYS guard, which 403s a write to
  // those fields from anyone who is neither superuser nor staff.
  const isPlatformStaff = Boolean(user?.is_superuser || user?.is_staff);

  const visibleSections = SECTIONS.filter(
    s => (!s.superOnly || isSuperuser) && (!s.staffOnly || isPlatformStaff),
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Platform Admin</h1>
          <p className="text-muted-foreground text-sm mt-1">Internal platform configuration — Helix team only</p>
        </div>
        <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold uppercase">VC Internal</span>
      </div>

      <div className="flex gap-6">
        {/* Left sidebar */}
        <nav className="w-56 shrink-0 space-y-1 sticky top-6 self-start max-h-[calc(100vh-8rem)] overflow-y-auto">
          {visibleSections.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveSection(key)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                activeSection === key
                  ? 'bg-primary/10 text-primary font-semibold'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground'
              }`}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{label}</span>
              {activeSection === key && <ChevronRight className="h-3 w-3 ml-auto" />}
            </button>
          ))}
        </nav>

        {/* Content area */}
        <div className="flex-1 min-w-0">
          {activeSection === 'platform' && (
            <PlatformTab
              platformSettings={platformSettings} saving={saving}
              onPlatformSettingsChange={setPlatformSettings} onSave={savePlatformSettings}
              purchasingTabs={purchasingTabs} onPurchasingTabsChange={setPurchasingTabs} onSavePurchasingTabs={savePurchasingTabs}
              qualityTabs={qualityTabs} onQualityTabsChange={setQualityTabs} onSaveQualityTabs={saveQualityTabs}
              dashboardTabs={dashboardTabs} onDashboardTabsChange={setDashboardTabs}
              supplierDashboardTabs={supplierDashboardTabs} onSupplierDashboardTabsChange={setSupplierDashboardTabs}
              onSaveDashboardTabs={saveDashboardTabs}
            />
          )}
          {activeSection === 'regional-defaults' && <RegionalDefaultsTab />}
          {activeSection === 'writeback' && isPlatformStaff && (
            <WritebackRulesSection
              platformSettings={platformSettings} saving={saving}
              onPlatformSettingsChange={setPlatformSettings} onSave={savePlatformSettings}
            />
          )}
          {activeSection === 'features' && (
            <FeaturesTab
              platformSettings={platformSettings} barrattFlags={barrattFlags} saving={saving}
              // CR-6274: the GRN Repair row needs the Scheduled tab state so it
              // can say why it is inert rather than looking broken.
              // CR-8725: the SAVED value. A Features save no longer stores the
              // dashboard tabs, so an unsaved Scheduled switch must not unlock it.
              // CR-9099: the platform value, not the viewed site's override.
              scheduledTabEnabled={savedDashboardTabs['scheduled'] === true}
              onPlatformSettingsChange={setPlatformSettings} onBarrattFlagsChange={setBarrattFlags} onSave={savePlatformSettings}
            />
          )}
          {activeSection === 'supplier-card' && (
            <SupplierCardTab supplierCardFields={supplierCardFields} saving={saving} onSupplierCardFieldsChange={setSupplierCardFields} onSave={saveSupplierCardFields} />
          )}
          {activeSection === 'supplier-profile' && <SupplierProfileLayoutTab />}
          {activeSection === 'data-import' && (
            <DataImportTilesTab dataImportTiles={dataImportTiles} saving={saving} onDataImportTilesChange={setDataImportTiles} onSave={saveDataImportTiles} />
          )}
          {activeSection === 'analytics' && <AnalyticsTab />}
          {activeSection === 'system-schedule' && (
            <>
              <SystemScheduleTab />
              {/* CR-4751: when the nightly Cached Performance snapshot runs. */}
              <div className="mt-6">
                <DashboardSnapshotScheduleSection />
              </div>
              {/* METRIC-DEFINITIONS Q12/Q20: arrears snapshot schedules —
                  group-level one-instant capture with per-site overrides. */}
              <div className="mt-6">
                <ArrearsSnapshotScheduleSection />
              </div>
              {/* FORECAST-FLUCTUATION: weekly forecast copy schedule per site
                  (PO-line-grain sites; feeds the Forecast Stability sub-tab). */}
              <div className="mt-6">
                <ForecastSnapshotScheduleSection />
              </div>
            </>
          )}
          {activeSection === 'command-runner' && <CommandRunnerTab />}
          {activeSection === 'help' && isSuperuser && <HelpLayoutTab />}
        </div>
      </div>
    </div>
  );
}
